import { Box, Button, Typography, alpha, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import authActions from 'store/app/auth/action';
import isImpersonatingSelector from 'store/selectors/isImpersonating';
import { getImpersonationTargetLabel } from 'utils/impersonationDisplayName';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';

const ImpersonationBanner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isImpersonating = useSelector(isImpersonatingSelector);
  const auth = useSelector(state => state.auth);
  const exitImpersonation = useActions(authActions.exitImpersonation);

  const bannerSx = useMemo(
    () => ({
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: 1.5,
      px: 2,
      py: 0.75,
      backgroundColor:
        theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.18) : '#FEF3C7',
      borderBottom: `1px solid ${
        theme.palette.mode === 'dark' ? theme.palette.warning.dark : '#F59E0B'
      }`,
      color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#92400E',
      '@media print': {
        display: 'none',
      },
    }),
    [
      theme.palette.mode,
      theme.palette.warning.dark,
      theme.palette.warning.light,
      theme.palette.warning.main,
    ],
  );

  const buttonSx = useMemo(
    () => ({
      color: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#92400E',
      borderColor: theme.palette.mode === 'dark' ? theme.palette.warning.main : '#D97706',
      textTransform: 'none',
      minWidth: 'auto',
      py: 0.25,
      '&:hover': {
        borderColor: theme.palette.mode === 'dark' ? theme.palette.warning.light : '#B45309',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.warning.main, 0.12)
            : 'rgba(217, 119, 6, 0.08)',
      },
    }),
    [theme.palette.mode, theme.palette.warning.light, theme.palette.warning.main],
  );

  const targetLabel = getImpersonationTargetLabel(auth);

  const handleExit = useCallback(async () => {
    await exitImpersonation({ reason: 'user' });
    navigate('/home/messages');
  }, [exitImpersonation, navigate]);

  if (!isImpersonating) {
    return null;
  }

  return (
    <Box sx={bannerSx} role="status" aria-live="polite">
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
        {I18n.t('Impersonation.BannerLabel', { name: targetLabel })}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleExit}
        sx={buttonSx}
        data-testid="impersonation-exit"
      >
        {I18n.t('Impersonation.BackToSuperadmin')}
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
